import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Text } from '@/components/Text';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaCrown, FaStar } from 'react-icons/fa';

interface SubscriptionCTAProps {
  onSubscribe: (isYearly: boolean) => void;
  plusPlan: {
    monthly: number;
    yearly: number;
  };
}

export const SubscriptionCTA = ({
  onSubscribe,
  plusPlan,
}: SubscriptionCTAProps) => {
  const { t } = useTranslation();
  const [isYearly, setIsYearly] = useState(true); // Default to yearly

  const handleSubscribe = () => {
    onSubscribe(isYearly);
  };

  return (
    <Card className="relative overflow-hidden bg-gradient-to-br from-sky-600 via-linko-light to-sky-500 p-4 text-white md:p-6">
      {/* Background decoration */}
      <div className="absolute right-0 top-0 -translate-y-4 translate-x-4">
        <div className="size-32 rounded-full bg-white/10"></div>
      </div>
      <div className="absolute bottom-0 left-0 -translate-x-8 translate-y-8">
        <div className="size-24 rounded-full bg-linko-dark/20"></div>
      </div>

      <div className="relative">
        <div className="mb-4 flex flex-col items-start justify-between gap-4 md:mb-6 md:flex-row md:gap-0">
          <div>
            <div className="mb-3 inline-flex items-center gap-2">
              <div className="rounded-lg bg-gradient-to-r from-linko-light to-sky-400 p-2 shadow-lg">
                <FaCrown className="text-lg text-white" />
              </div>
              <span className="rounded-full bg-linko-dark/30 px-3 py-1 text-xs font-semibold text-sky-100">
                PREMIUM
              </span>
            </div>
            <Text className="mb-2 text-xl font-bold text-white md:text-2xl">
              {t('settings:upgrade.title')}
            </Text>
            <Text className="mb-3 text-sky-100 md:mb-4">
              {t('settings:upgrade.description')}
            </Text>
          </div>

          {/* Billing Toggle */}
          <div className="flex w-full items-center justify-center md:w-auto md:justify-end">
            <motion.div
              className="flex items-center rounded-full bg-white/10 p-1 backdrop-blur-sm"
              layout
            >
              <motion.button
                onClick={() => setIsYearly(false)}
                className={`relative px-3 py-1.5 text-sm font-medium transition-colors ${
                  !isYearly ? 'text-sky-800' : 'text-white'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {!isYearly && (
                  <motion.div
                    className="absolute inset-0 rounded-full bg-white shadow-lg"
                    layoutId="billing-toggle"
                    transition={{
                      type: 'spring' as const,
                      stiffness: 500,
                      damping: 30,
                    }}
                  />
                )}
                <span className="relative z-10">
                  {t('settings:billing.monthly')}
                </span>
              </motion.button>

              <motion.button
                onClick={() => setIsYearly(true)}
                className={`relative px-3 py-1.5 text-sm font-medium transition-colors ${
                  isYearly ? 'text-sky-800' : 'text-white'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {isYearly && (
                  <motion.div
                    className="absolute inset-0 rounded-full bg-white shadow-lg"
                    layoutId="billing-toggle"
                    transition={{
                      type: 'spring' as const,
                      stiffness: 500,
                      damping: 30,
                    }}
                  />
                )}
                <span className="relative z-10">
                  {t('settings:billing.yearly')}
                </span>
              </motion.button>
            </motion.div>
          </div>
        </div>

        {/* Features list */}
        <div className="mb-4 grid grid-cols-1 gap-2 md:mb-6 md:gap-3">
          {[
            'businessCards',
            'accessoryConnection',
            'linkManagement',
            'contactManagement',
            'followUpEmail',
            'leadCapture',
            'statistics',
          ].map(featureKey => (
            <div key={featureKey} className="flex items-center space-x-2">
              <div className="rounded-full bg-sky-400 p-1 shadow-sm">
                <FaCheck className="text-xs text-white" />
              </div>
              <span className="text-xs text-sky-50 md:text-sm">
                {t(`settings:upgrade.features.${featureKey}`)}
              </span>
            </div>
          ))}
        </div>

        {/* Price Display */}
        <div className="mb-4 text-center">
          <div className="flex items-center justify-center gap-3">
            <div>
              <span className="text-2xl font-bold text-white">
                {isYearly ? plusPlan.yearly : plusPlan.monthly}€
              </span>
              <span className="ml-1 text-sky-100">
                /{t(`settings:billing.${isYearly ? 'month' : 'month'}`)}
              </span>
            </div>

            <AnimatePresence>
              {isYearly && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.8, x: -10 }}
                  animate={{ opacity: 1, scale: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.8, x: -10 }}
                  transition={{ duration: 0.2 }}
                  className="rounded-full bg-green-500/80 px-2 py-1 text-xs font-semibold text-white"
                >
                  {t('settings:upgrade.save')}
                </motion.span>
              )}
            </AnimatePresence>
          </div>

          {isYearly && (
            <div className="mt-1">
              <span className="text-sm text-sky-200">
                {t('settings:upgrade.billedAnnually', {
                  amount: (plusPlan.yearly * 12).toFixed(2),
                })}
              </span>
            </div>
          )}
        </div>

        <Button
          onClick={handleSubscribe}
          className="w-full bg-primary py-3 text-base font-bold text-secondary shadow-xl transition-colors hover:bg-white md:py-4 md:text-lg"
        >
          <FaStar className="mr-2" />
          {t('settings:upgrade.button')}
        </Button>
      </div>
    </Card>
  );
};
